// Copyright 2026 Bitech.id. Licensed under the MIT License.

import { generateUuidV7 } from './uuid7.js';
import type {
  ResolvedStreamOptions,
  StreamLimits,
  StreamMode,
  StreamSessionState,
} from './types.js';
import { DEFAULT_LIMITS } from './types.js';

const STORAGE_KEY = '__biwave_stream_session__';

function readStorage(): StreamSessionState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StreamSessionState;
  } catch {
    return null;
  }
}

function writeStorage(state: StreamSessionState): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // best-effort — a private-browsing quota error must never bubble up
  }
}

function clearStorage(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort
  }
}

/** Sampling roll for a brand-new session — recording, buffered (error-only), or inactive. */
function rollMode(options: ResolvedStreamOptions): StreamMode | null {
  if (Math.random() < options.sessionSampleRate) return 'recording';
  if (Math.random() < options.errorSampleRate) return 'buffered';
  return null;
}

/**
 * Owns Stream session lifecycle: sampling, sessionStorage persistence (so SPA
 * navigation/reload in the same tab resumes the same session), seq counter,
 * and expiry against the limits the backend returned from init.
 */
export class SessionManager {
  private state: StreamSessionState | null = null;

  constructor(private readonly options: ResolvedStreamOptions) {}

  /** Resumes a still-valid session from this tab, or rolls sampling for a new one. Returns null if this tab isn't recording or buffering at all. */
  start(): StreamSessionState | null {
    const existing = readStorage();
    if (existing && !this.isExpiredState(existing)) {
      this.state = existing;
      return this.state;
    }

    const mode = rollMode(this.options);
    if (!mode) {
      clearStorage();
      return null;
    }

    this.state = {
      session_id: generateUuidV7(),
      mode,
      started_at: Date.now(),
      next_seq: 0,
      limits: null,
      init_sent: false,
    };
    writeStorage(this.state);
    return this.state;
  }

  getState(): StreamSessionState | null {
    return this.state;
  }

  getLimits(): StreamLimits {
    return this.state?.limits ?? DEFAULT_LIMITS;
  }

  setLimits(limits: StreamLimits): void {
    if (!this.state) return;
    this.state.limits = limits;
    this.state.init_sent = true;
    writeStorage(this.state);
  }

  /** Allocates the next seq — monotonic, never reused for different content. */
  allocateSeq(): number {
    if (!this.state) throw new Error('no active stream session');
    const seq = this.state.next_seq;
    this.state.next_seq += 1;
    writeStorage(this.state);
    return seq;
  }

  /** Buffered -> recording, on a promote-on-error trigger. Keeps the same session_id/seq counter. */
  promoteToRecording(): void {
    if (!this.state || this.state.mode === 'recording') return;
    this.state.mode = 'recording';
    writeStorage(this.state);
  }

  isExpired(): boolean {
    return this.state ? this.isExpiredState(this.state) : true;
  }

  private isExpiredState(state: StreamSessionState): boolean {
    const limits = state.limits ?? DEFAULT_LIMITS;
    if (Date.now() - state.started_at >= limits.max_session_duration_ms) return true;
    if (state.next_seq >= limits.max_chunks) return true;
    return false;
  }

  end(): void {
    this.state = null;
    clearStorage();
  }
}
